import React from "react";
import { useSelector } from "react-redux";

const EventCostSummary = () => {
  const selectedEvent = useSelector(
    (state) => state.eventOrganiser.selectedEvent
  );

  if (!selectedEvent || !selectedEvent.activities) return null;

  const getTotalCost = () => {
    let total = 0;

    selectedEvent.activities.forEach((activity) => {
      (activity.cost_sheets || []).forEach((sheet) => {
        if (sheet.cst_status === "1") {
          (sheet.items || []).forEach((item) => {
            if (item.parentcode === "cmkey_boq_bom") {
              total +=
                parseFloat(item.total_cost || 0) * parseFloat(item.nos || 1);
            }
          });
        }
      });
    });

    return total;
  };

  const tableStyle = {
    borderCollapse: "collapse",
    width: "90%",
    marginTop: "10px",
    fontFamily: "Arial, sans-serif",
    fontSize: "14px",
  };

  const thStyle = {
    border: "1px solid #ddd",
    padding: "8px",
    background: "#f4f4f4",
    textAlign: "left",
  };

  const tdStyle = {
    border: "1px solid #ddd",
    padding: "5px",
    verticalAlign: "top",
  };

  const boldTd = {
    ...tdStyle,
    fontWeight: "bold",
    background: "#fafafa",
  };

  const subtotalRowStyle = {
    backgroundColor: "#f9f9f9",
    fontWeight: "bold",
  };

  const buildTree = (items) => {
    const itemMap = {};
    const clonedItems = items.map((item) => ({ ...item, children: [] }));
    const roots = [];

    clonedItems.forEach((item) => {
      itemMap[item.code] = item;
    });

    clonedItems.forEach((item) => {
      if (item.parentcode && itemMap[item.parentcode]) {
        itemMap[item.parentcode].children.push(item);
      } else {
        roots.push(item);
      }
    });

    return roots;
  };

  const renderHierarchyRows = (item, level = 0) => {
    const linePrefix = level === 0 ? "•" : "└─";
    const indent = "\u00A0".repeat(level * 4);
    const rows = [
      <tr key={item.code}>
        <td style={{ ...tdStyle, whiteSpace: "pre" }}>
          {indent + linePrefix + " " + item.name}
        </td>
        <td style={tdStyle}>{item.nos || 1}</td>
        <td style={tdStyle}>₹{item.total_cost || 0}</td>
        <td style={tdStyle}>₹{(item.total_cost || 0) * (item.nos || 1)}</td>
      </tr>,
    ];

    if (item.children?.length) {
      item.children.forEach((child) => {
        rows.push(...renderHierarchyRows(child, level + 1));
      });
    }

    return rows;
  };

  const calculateActivityRowSpan = (activity) => {
    const validSheets = (activity.cost_sheets || []).filter(
      (sheet) => sheet.cst_status === "1"
    );

    if (validSheets.length === 0) return 0;

    let rowCount = 0;
    validSheets.forEach((sheet) => {
      const items = sheet.items || [];
      const tree = buildTree(items);
      const hierarchyRows = tree.flatMap((item) => renderHierarchyRows(item));
      rowCount += hierarchyRows.length + 1; // +1 for the subtotal row
    });

    return rowCount;
  };

  return (
    <div style={{ maxHeight: "70vh", overflowY: "auto" }}>
      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={thStyle}>Activity Name</th>
            <th style={thStyle}>Cost Sheet Title</th>
            <th style={thStyle}>Item Name</th>
            <th style={thStyle}>Qty</th>
            <th style={thStyle}>Price (₹)</th>
            <th style={thStyle}>Total (₹)</th>
          </tr>
        </thead>
        <tbody>
          {selectedEvent.activities.map((activity, actIdx) => {
            const validSheets = (activity.cost_sheets || []).filter(
              (sheet) => sheet.cst_status === "1"
            );
            if (validSheets.length === 0) return null;

            const activityRowSpan = calculateActivityRowSpan(activity);
            let activityRendered = false;

            return validSheets.flatMap((sheet, sheetIndex) => {
              const items = sheet.items || [];
              const tree = buildTree(items);
              const hierarchyRows = tree.flatMap((item) =>
                renderHierarchyRows(item)
              );

              const sheetRowSpan = hierarchyRows.length;
              const subtotal = items.reduce((sum, item) => {
                if (item.parentcode === "cmkey_boq_bom") {
                  return (
                    sum +
                    parseFloat(item.total_cost || 0) * parseFloat(item.nos || 1)
                  );
                }
                return sum;
              }, 0);

              const rows = hierarchyRows.map((row, i) => {
                const baseCells = [];

                if (!activityRendered && i === 0) {
                  baseCells.push(
                    <td
                      style={tdStyle}
                      rowSpan={activityRowSpan}
                      key={`activity-${activity.name}`}
                    >
                      {activity.name}
                    </td>
                  );
                  activityRendered = true;
                }

                if (i === 0) {
                  baseCells.push(
                    <td
                      style={tdStyle}
                      rowSpan={sheetRowSpan + 1} // +1 for the subtotal row
                      key={`sheet-${sheet.title}`}
                    >
                      {sheet.title}
                    </td>
                  );
                }

                return React.cloneElement(row, {
                  children: [...baseCells, ...row.props.children],
                });
              });

              // Add subtotal row
              rows.push(
                <tr key={`subtotal-${sheet.id}`} style={subtotalRowStyle}>
                  {!activityRendered && (
                    <td style={tdStyle} rowSpan={activityRowSpan}>
                      {activity.name}
                    </td>
                  )}
                  {!activityRendered && (
                    <td style={tdStyle} rowSpan={sheetRowSpan + 1}>
                      {sheet.title}
                    </td>
                  )}
                  <td style={tdStyle} colSpan={3}>
                    Subtotal for {sheet.title}
                  </td>
                  <td style={tdStyle}>₹{subtotal}</td>
                </tr>
              );

              return rows;
            });
          })}

          {/* Final Total */}
          <tr>
            <td style={boldTd} colSpan="5" align="right">
              Total
            </td>
            <td style={boldTd}>₹{getTotalCost()}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export default EventCostSummary;
